import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTenantsParking } from 'contexts/TenantsParkingContext';
import { AppState } from 'reducers/reducers';
import { SpotInterface, TenantsParkingData } from 'types';
import Loader from 'components/Loader/Loader';
import AGMessage from 'components/AGMessage/AGMessage';
import BuildingIcon from 'components/Icons/BuildingIcon';
import styles from './TenantsParking.module.css';
import Table from './Table';
import TenantBulkSlotsModal from './TenantBulkSlotsModal';
import { TenantNameSort } from 'utils/constants';

type TenantsParkingProps = {
    spot: SpotInterface;
    filters: {
        tenantOrdering: TenantNameSort;
    };
    urlUpdater: { tenantOrdering: (value: string | null) => void };
};

function apiOrderingFromTenantSort(sort: TenantNameSort): string | undefined {
    if (sort === 'asc') return 'description';
    if (sort === 'desc') return '-description';
    return undefined;
}

const TenantsParking = ({ spot, filters: { tenantOrdering }, urlUpdater }: TenantsParkingProps) => {
    const { getTenantsParking, loading, errorMessage, tenantsParkings } = useTenantsParking();
    const [bulkSlotsTenant, setBulkSlotsTenant] = useState<TenantsParkingData | null>(null);
    const isDemoMode = useSelector((state: AppState) => state.dashboard.isDemoMode);

    useEffect(() => {
        getTenantsParking(spot.uuid, apiOrderingFromTenantSort(tenantOrdering));
    }, [spot.uuid, tenantOrdering, getTenantsParking]);

    const cycleNameSort = () => {
        if (tenantOrdering === null) {
            urlUpdater.tenantOrdering('asc');
        } else if (tenantOrdering === 'asc') {
            urlUpdater.tenantOrdering('desc');
        } else {
            urlUpdater.tenantOrdering(null);
        }
    };

    if (loading) return <Loader />;

    if (tenantsParkings.length === 0)
        return (
            <div className={styles.noData}>
                <div className={styles.noDataHeader}>
                    <BuildingIcon /> <p>Tenant billing is not enabled at your property</p>
                </div>
                <div className={styles.noDataContent}>
                    <p>What's a tenant?</p>
                    <p>A tenant is a business account that uses your parking facility each month.</p>
                    <p>Reach out to partners@airgarage.com if you think your property may be eligible.</p>
                </div>
            </div>
        );

    return (
        <div style={{ width: '100%' }}>
            {!!errorMessage && <AGMessage color="error" title={errorMessage} />}
            <Table
                data={tenantsParkings}
                nameSort={tenantOrdering}
                onNameSortClick={cycleNameSort}
                onRowClick={(row) => setBulkSlotsTenant(row)}
                isDemoMode={isDemoMode}
            />
            {bulkSlotsTenant && (
                <TenantBulkSlotsModal
                    key={bulkSlotsTenant.uuid}
                    tenant={bulkSlotsTenant}
                    onClose={() => setBulkSlotsTenant(null)}
                    isDemoMode={isDemoMode}
                />
            )}
        </div>
    );
};

export default TenantsParking;
